import React from 'react';
import { 
    CalendarDays, 
    FileText, 
    FileSpreadsheet,
    MonitorDot, 
    PackagePlus, 
    ScrollText, 
    UserRound, 
    Folder, 
    CheckCircle, 
    Settings, 
    MessageCircle,
    Building2,
    UserCog,
    Shield,
    Users,
    Clock,
    MapPin,
    ClipboardList,
    Calendar,
    Receipt,
    DollarSign,
    CreditCard,
    Cog,
    MessageSquare,
    Mail,
    HelpCircle,
    Navigation,
    AlertTriangle,
    Activity,
    Home,
    User,
    Package,
    Route,
    Codesandbox,
    Search,
    Video,
    QrCode,
    SlidersHorizontal
} from "lucide-react";

interface MenuPermission {
    id: string;
    menu_key: string;
    menu_label: string;
    menu_icon: string;
    menu_path: string;
    menu_order: number;
    parent_menu: string | null;
    is_active: boolean;
    description?: string;
}

interface MenuItem {
    id: string;
    label: string;
    link: string;
    icon?: React.ReactElement;
    className: string;
    subItems?: MenuItem[];
    isTitle?: boolean;
    parentId?: number;
    roles?: string[];
    dynamicLink?: boolean;
    order?: number;
}

// Icon mapping from backend to frontend components
const iconMap: { [key: string]: React.ReactElement } = {
    'MonitorDot': React.createElement(MonitorDot, { className: "text-blue-600 hover:text-blue-700 transition-colors" }),
    'Home': React.createElement(Home, { className: "text-blue-600 hover:text-blue-700 transition-colors" }),
    'Building2': React.createElement(Building2, { className: "text-slate-600 hover:text-slate-700 transition-colors" }),
    'UserCog': React.createElement(UserCog, { className: "text-violet-600 hover:text-violet-700 transition-colors" }),
    'Shield': React.createElement(Shield, { className: "text-rose-600 hover:text-rose-700 transition-colors" }),
    'Users': React.createElement(Users, { className: "text-emerald-600 hover:text-emerald-700 transition-colors" }),
    'Mail': React.createElement(Mail, { className: "text-sky-600 hover:text-sky-700 transition-colors" }),
    'MessageCircle': React.createElement(MessageCircle, { className: "text-teal-500 hover:text-teal-600 transition-colors" }),
    'ScrollText': React.createElement(ScrollText, { className: "text-amber-500 hover:text-amber-600 transition-colors" }),
    'Calendar': React.createElement(Calendar, { className: "text-pink-500 hover:text-pink-600 transition-colors" }),
    'Clock': React.createElement(Clock, { className: "text-blue-500 hover:text-blue-600 transition-colors" }),
    'ClipboardList': React.createElement(ClipboardList, { className: "text-emerald-500 hover:text-emerald-600 transition-colors" }),
    'CalendarDays': React.createElement(CalendarDays, { className: "text-blue-500 hover:text-blue-600 transition-colors" }),
    'FileText': React.createElement(FileText, { className: "text-indigo-500 hover:text-indigo-600 transition-colors" }),
    'User': React.createElement(User, { className: "text-purple-500 hover:text-purple-600 transition-colors" }),
    'Package': React.createElement(Package, { className: "text-orange-500 hover:text-orange-600 transition-colors" }),
    'CheckCircle': React.createElement(CheckCircle, { className: "text-green-500 hover:text-green-600 transition-colors" }),
    'Settings': React.createElement(Settings, { className: "text-gray-500 hover:text-gray-600 transition-colors" }),
    'Folder': React.createElement(Folder, { className: "text-yellow-500 hover:text-yellow-600 transition-colors" }),
    'Receipt': React.createElement(Receipt, { className: "text-red-500 hover:text-red-600 transition-colors" }),
    'DollarSign': React.createElement(DollarSign, { className: "text-green-500 hover:text-green-600 transition-colors" }),
    'CreditCard': React.createElement(CreditCard, { className: "text-blue-500 hover:text-blue-600 transition-colors" }),
    'Cog': React.createElement(Cog, { className: "text-gray-500 hover:text-gray-600 transition-colors" }),
    'MessageSquare': React.createElement(MessageSquare, { className: "text-blue-500 hover:text-blue-600 transition-colors" }),
    'HelpCircle': React.createElement(HelpCircle, { className: "text-orange-500 hover:text-orange-600 transition-colors" }),
    'Navigation': React.createElement(Navigation, { className: "text-purple-500 hover:text-purple-600 transition-colors" }),
    'AlertTriangle': React.createElement(AlertTriangle, { className: "text-red-500 hover:text-red-600 transition-colors" }),
    'Activity': React.createElement(Activity, { className: "text-green-500 hover:text-green-600 transition-colors" }),
    'Route': React.createElement(Route, { className: "text-blue-500 hover:text-blue-600 transition-colors" }),
    'Codesandbox': React.createElement(Codesandbox, { className: "text-indigo-500 hover:text-indigo-600 transition-colors" }),
    'PackagePlus': React.createElement(PackagePlus, { className: "text-purple-500 hover:text-purple-600 transition-colors" }),
    'UserRound': React.createElement(UserRound, { className: "text-pink-500 hover:text-pink-600 transition-colors" }),
    'MapPin': React.createElement(MapPin, { className: "text-red-500 hover:text-red-600 transition-colors" }),
    'Search': React.createElement(Search, { className: "text-purple-500 hover:text-purple-600 transition-colors" }),
    'Video': React.createElement(Video, { className: "text-red-500 hover:text-red-600 transition-colors" }),
    'QrCode': React.createElement(QrCode, { className: "text-emerald-500 hover:text-emerald-600 transition-colors" }),
    'FileSpreadsheet': React.createElement(FileSpreadsheet, { className: "text-green-600 hover:text-green-700 transition-colors" })
};

// Color mapping for different menu categories
const getColorClass = (menuKey: string): string => {
    const colorMap: { [key: string]: string } = {
        'dashboard': 'blue',
        'departments': 'slate',
        'designations': 'violet',
        'roles': 'rose',
        'employees': 'emerald',
        'email': 'sky',
        'engage': 'teal',
        'notes': 'amber',
        'calendar': 'pink',
        'attendance': 'blue',
        'daily-updates': 'emerald',
        'leaves': 'indigo',
        'projects': 'purple',
        'expenses': 'orange',
        'loans': 'green',
        'assets': 'yellow',
        'invoices': 'red',
        'salary': 'green',
        'feedback': 'orange',
        'help': 'orange',
        'settings': 'gray',
        'profile': 'purple',
        'chat': 'blue',
        'notifications': 'red',
        'reports': 'green',
        'analytics': 'blue',
        'crm': 'indigo',
        'visits': 'purple',
        'geofence': 'red',
        'anomaly-detection': 'red',
        'crashlytics': 'orange',
        'tickets': 'blue',
        'training': 'purple',
        'performance': 'green',
        'policies': 'gray',
        'wellbeing': 'pink',
        'visitors': 'yellow',
        'contacts': 'blue',
        'activities': 'green',
        'module-assignment': 'purple',
        'search': 'purple',
        'interviews': 'red',
        'interview-recordings': 'red',
        'qr-code-generator': 'emerald',
        'pdf-to-csv': 'green'
    };

    /**
     * IMPORTANT (Tailwind):
     * Avoid dynamic classnames like `hover:bg-${color}-50` because Tailwind can't
     * statically analyze them and will purge those styles in production builds.
     */
    const color = colorMap[menuKey] || 'blue';

    const colorClasses: Record<string, string> = {
        // Add a clear active indicator (left border) without changing routing/roles.
        blue: "hover:bg-blue-50 border-l-4 border-transparent hover:border-blue-500 [&.active]:border-blue-500 transition-all duration-200",
        slate: "hover:bg-slate-50 border-l-4 border-transparent hover:border-slate-500 [&.active]:border-slate-500 transition-all duration-200",
        violet: "hover:bg-violet-50 border-l-4 border-transparent hover:border-violet-500 [&.active]:border-violet-500 transition-all duration-200",
        rose: "hover:bg-rose-50 border-l-4 border-transparent hover:border-rose-500 [&.active]:border-rose-500 transition-all duration-200",
        emerald: "hover:bg-emerald-50 border-l-4 border-transparent hover:border-emerald-500 [&.active]:border-emerald-500 transition-all duration-200",
        sky: "hover:bg-sky-50 border-l-4 border-transparent hover:border-sky-500 [&.active]:border-sky-500 transition-all duration-200",
        teal: "hover:bg-teal-50 border-l-4 border-transparent hover:border-teal-500 [&.active]:border-teal-500 transition-all duration-200",
        amber: "hover:bg-amber-50 border-l-4 border-transparent hover:border-amber-500 [&.active]:border-amber-500 transition-all duration-200",
        pink: "hover:bg-pink-50 border-l-4 border-transparent hover:border-pink-500 [&.active]:border-pink-500 transition-all duration-200",
        indigo: "hover:bg-indigo-50 border-l-4 border-transparent hover:border-indigo-500 [&.active]:border-indigo-500 transition-all duration-200",
        purple: "hover:bg-purple-50 border-l-4 border-transparent hover:border-purple-500 [&.active]:border-purple-500 transition-all duration-200",
        orange: "hover:bg-orange-50 border-l-4 border-transparent hover:border-orange-500 [&.active]:border-orange-500 transition-all duration-200",
        green: "hover:bg-green-50 border-l-4 border-transparent hover:border-green-500 [&.active]:border-green-500 transition-all duration-200",
        yellow: "hover:bg-yellow-50 border-l-4 border-transparent hover:border-yellow-500 [&.active]:border-yellow-500 transition-all duration-200",
        red: "hover:bg-red-50 border-l-4 border-transparent hover:border-red-500 [&.active]:border-red-500 transition-all duration-200",
        gray: "hover:bg-gray-50 border-l-4 border-transparent hover:border-gray-500 [&.active]:border-gray-500 transition-all duration-200",
    };

    return colorClasses[color] || colorClasses.blue;
};

// Convert backend menu data to frontend menu structure
export const buildMenuFromPermissions = (menuPermissions: MenuPermission[]): MenuItem[] => {
    if (!menuPermissions || menuPermissions.length === 0) {
        return [];
    }
    
    // Ensure Dashboard is always included
    const hasDashboard = menuPermissions.some(permission => permission.menu_key === 'dashboard');
    let processedPermissions = [...menuPermissions]; // Create a copy to avoid modifying the original array
    
    if (!hasDashboard) {
        const defaultDashboard: MenuPermission = {
            id: 'dashboard-default',
            menu_key: 'dashboard',
            menu_label: 'Dashboard',
            menu_icon: 'MonitorDot',
            menu_path: '/dashboard',
            menu_order: 0,
            parent_menu: null,
            is_active: true,
            description: 'Main dashboard view'
        };
        processedPermissions.unshift(defaultDashboard);
    }

    // Get current user role from localStorage
    const getCurrentUserRole = (): string => {
        try {
            const userDetails = localStorage.getItem('userDetails');
            if (userDetails) {
                const user = JSON.parse(userDetails);
                return user.role || '';
            }
            return '';
        } catch (error) {
            console.error('Error parsing user details:', error);
            return '';
        }
    };

    const currentUserRole = getCurrentUserRole();
    const isSuperAdmin = currentUserRole === 'Super Admin';
    const isAdmin = currentUserRole === 'Admin';
    const isPrivilegedAdmin = isSuperAdmin || isAdmin;

    // Sort by menu_order
    const sortedMenus = [...processedPermissions].sort((a, b) => a.menu_order - b.menu_order);

    // Group by parent_menu
    const menuGroups: { [key: string]: MenuPermission[] } = {};
    const rootMenus: MenuPermission[] = [];

    const isAllowedMenu = (menu: MenuPermission): boolean => {
        // Filter out module-assignment for non-super-admin users
        if (menu.menu_key === 'module-assignment' && !isSuperAdmin) {
            return false;
        }

        // Filter out module-management for non-super-admin users
        if (menu.menu_key === 'module-management' && !isSuperAdmin) {
            return false;
        }

        // Filter out search for non-privileged admins (Admin + Super Admin)
        if (menu.menu_key === 'search' && !isPrivilegedAdmin) {
            return false;
        }

        // Filter out QR Code Generator for non-privileged admins (Admin + Super Admin)
        if (menu.menu_key === 'qr-code-generator' && !isPrivilegedAdmin) {
            return false;
        }

        // Filter out PDF to CSV for non-privileged admins (Admin + Super Admin)
        if (menu.menu_key === 'pdf-to-csv' && !isPrivilegedAdmin) {
            return false;
        }

        // Remove Projects and Tasks from the navbar
        if (menu.menu_key === 'projects' || menu.menu_key === 'tasks') {
            return false;
        }

        return true;
    };

    const allowedMenus = sortedMenus.filter(isAllowedMenu);
    const allowedKeys = new Set(allowedMenus.map(m => m.menu_key));

    allowedMenus.forEach(menu => {
        // If API returns a parent_menu but doesn't include that parent in the list,
        // promote the item to root so it doesn't get dropped.
        const parentKey = menu.parent_menu;
        const hasValidParent = parentKey ? allowedKeys.has(parentKey) : false;

        if (parentKey && hasValidParent) {
            if (!menuGroups[parentKey]) {
                menuGroups[parentKey] = [];
            }
            menuGroups[parentKey].push(menu);
        } else {
            rootMenus.push(menu);
        }
    });

    // Convert to frontend menu structure
    const normalizeMenuLabel = (menu: MenuPermission): string => {
        if (menu.menu_key === 'qr-code-generator') return 'QR Code Generator';
        if (menu.menu_key === 'pdf-to-csv') return 'PDF to CSV';
        return menu.menu_label;
    };

    const convertToMenuItem = (menu: MenuPermission): MenuItem => {
        const icon = iconMap[menu.menu_icon] || React.createElement(Settings, { className: "text-gray-500 hover:text-gray-600 transition-colors" });
        const colorClass = getColorClass(menu.menu_key);

        const menuItem: MenuItem = {
            id: menu.menu_key,
            label: normalizeMenuLabel(menu),
            link: menu.menu_path,
            icon: icon,
            className: colorClass,
            parentId: 2,
            roles: ['Admin', 'Super Admin', 'Manager', 'Employee', 'Staff', 'Intern'],
            order: menu.menu_order
        };

        // Add sub-items if they exist
        if (menuGroups[menu.menu_key]) {
            menuItem.subItems = menuGroups[menu.menu_key].map(subMenu => ({
                id: subMenu.menu_key,
                label: normalizeMenuLabel(subMenu),
                link: subMenu.menu_path,
                className: getColorClass(subMenu.menu_key)
            }));
        }

        return menuItem;
    };

    return rootMenus.map(convertToMenuItem);
};

// Add section titles to menu structure
export const addMenuSections = (menuItems: MenuItem[]): MenuItem[] => {
    // Maintain exact section order as LayoutMenuData.tsx
    const orderedSectionNames = [
        'Organization',
        'Communication',
        'Attendance & Location',
        'Updates & Reports',
        'HR Management',
        'Finance',
        'Feedback & Support',
        'Ticket Management',
        'Module Management',
        'Settings',
        'Tools & Settings'
    ];

    const sections: { [key: string]: MenuItem[] } = orderedSectionNames.reduce((acc, name) => {
        acc[name] = [];
        return acc;
    }, {} as { [key: string]: MenuItem[] });

    // Pull out Dashboard to place it first and avoid categorizing it into sections
    const dashboardItem = menuItems.find(i => i.id === 'dashboard');
    let itemsWithoutDashboard = menuItems.filter(i => i.id !== 'dashboard');

    // Determine role for conditional placement of Module Assignment
    let role = '';
    try {
        const userDetails = localStorage.getItem('userDetails');
        role = userDetails ? (JSON.parse(userDetails).role || '') : '';
    } catch (e) {
        role = '';
    }
    const isSuperAdmin = role === 'Super Admin';
    const isAdmin = role === 'Admin';
    const isPrivilegedAdmin = isSuperAdmin || isAdmin;

    const extractNestedItemById = (
        items: MenuItem[],
        targetId: string
    ): { extracted?: MenuItem; remaining: MenuItem[] } => {
        let extracted: MenuItem | undefined;

        const remaining: MenuItem[] = (items || [])
            .map(item => {
                if (!item) return item;
                if (item.id === targetId) {
                    extracted = extracted || item;
                    return null as any;
                }

                if (item.subItems && Array.isArray(item.subItems) && item.subItems.length > 0) {
                    const childResult = extractNestedItemById(item.subItems, targetId);
                    if (childResult.extracted && !extracted) {
                        extracted = childResult.extracted;
                    }

                    const nextSubItems = childResult.remaining;
                    // If a dropdown becomes empty after extraction, drop it too
                    if (!nextSubItems || nextSubItems.length === 0) {
                        const { subItems, ...rest } = item as any;
                        return rest as MenuItem;
                    }

                    return { ...item, subItems: nextSubItems };
                }

                return item;
            })
            .filter(Boolean) as MenuItem[];

        return { extracted, remaining };
    };

    // Extract Module Assignment item (if present)
    let moduleAssignmentItem = itemsWithoutDashboard.find(i => i.id === 'module-assignment');
    if (moduleAssignmentItem) {
        itemsWithoutDashboard = itemsWithoutDashboard.filter(i => i.id !== 'module-assignment');
    } else if (isSuperAdmin) {
        // Fabricate Module Assignment for super admins even if not returned by API
        moduleAssignmentItem = {
            id: 'module-assignment',
            label: 'Module Assignment',
            link: '/module-assignment',
            icon: React.createElement(Users, { className: 'text-purple-500 hover:text-purple-600 transition-colors' }),
            className: getColorClass('module-assignment'),
            parentId: 2,
            roles: ['Super Admin'],
            order: 1
        };
    }

    // Extract Module Management item (if present)
    let moduleManagementItem = itemsWithoutDashboard.find(i => i.id === 'module-management');
    if (moduleManagementItem) {
        itemsWithoutDashboard = itemsWithoutDashboard.filter(i => i.id !== 'module-management');
    } else if (isSuperAdmin) {
        // Fabricate Module Management for super admins even if not returned by API
        moduleManagementItem = {
            id: 'module-management',
            label: 'Module Management',
            link: '/module-management',
            icon: React.createElement(Settings, { className: 'text-gray-500 hover:text-gray-600 transition-colors' }),
            className: getColorClass('module-management'),
            parentId: 2,
            roles: ['Super Admin'],
            order: 2
        };
    }

    // Extract Search item (if present)
    let searchItem: MenuItem | undefined;
    ({ extracted: searchItem, remaining: itemsWithoutDashboard } = extractNestedItemById(itemsWithoutDashboard, 'search'));

    // Extract PDF to CSV item (if present)
    let pdfToCsvItem: MenuItem | undefined;
    ({ extracted: pdfToCsvItem, remaining: itemsWithoutDashboard } = extractNestedItemById(itemsWithoutDashboard, 'pdf-to-csv'));

    // Extract QR Code Generator item (if present)
    let qrCodeGeneratorItem: MenuItem | undefined;
    ({ extracted: qrCodeGeneratorItem, remaining: itemsWithoutDashboard } = extractNestedItemById(itemsWithoutDashboard, 'qr-code-generator'));

    // Interview Recordings and Interviews are now handled dynamically from backend modules
    // No need to extract or fabricate them here

    // Categorize menu items based on ids used in LayoutMenuData.tsx
    itemsWithoutDashboard.forEach(item => {
        if (['department-list', 'designation', 'role', 'employee'].includes(item.id)) {
            sections['Organization'].push(item);
        } else if (['email-list', 'engage', 'notes', 'calendar', 'chat'].includes(item.id)) {
            sections['Communication'].push(item);
        } else if (['attendance', 'attendance-sales', 'geofence-management', 'anomaly-detection', 'crashlytics', 'facial-recognition-approval', 'attendance-approvals'].includes(item.id)) {
            sections['Attendance & Location'].push(item);
        } else if (['daily-updates'].includes(item.id)) {
            sections['Updates & Reports'].push(item);
        } else if (['leaves', 'resumes', 'interviews', 'interview-recordings'].includes(item.id)) {
            sections['HR Management'].push(item);
        } else if (['expenses', 'payroll'].includes(item.id)) {
            sections['Finance'].push(item);
        } else if (['feedback', 'help-support'].includes(item.id)) {
            sections['Feedback & Support'].push(item);
        } else if (['my-tickets', 'all-tickets'].includes(item.id)) {
            sections['Ticket Management'].push(item);
        } else if (['settings'].includes(item.id)) {
            sections['Settings'].push(item);
        } else if (['profile'].includes(item.id)) {
            sections['Tools & Settings'].push(item);
        } else {
            // If an unknown item comes, keep it under Tools & Settings to avoid losing it
            sections['Tools & Settings'].push(item);
        }
    });

    // Sort items within each section by menu order if provided, else keep stable
    orderedSectionNames.forEach(section => {
        sections[section] = sections[section].sort((a, b) => {
            const ao = typeof (a as any).order === 'number' ? (a as any).order as number : Number.MAX_SAFE_INTEGER;
            const bo = typeof (b as any).order === 'number' ? (b as any).order as number : Number.MAX_SAFE_INTEGER;
            return ao - bo;
        });
    });

    // Build final menu with Dashboard first, then sections in order
    const finalMenu: MenuItem[] = [];

    // Add Dashboard first, without a section title
    if (dashboardItem) {
        finalMenu.push(dashboardItem);
    }

    // Immediately after Dashboard, show App Settings for Super Admin only
    if (isSuperAdmin) {
        finalMenu.push({
            id: 'app-settings',
            label: 'App Settings',
            link: '/app-settings',
            icon: React.createElement(SlidersHorizontal, { className: 'text-indigo-600 hover:text-indigo-700 transition-colors' }),
            className: 'hover:bg-indigo-50 border-l-4 border-transparent hover:border-indigo-500 [&.active]:border-indigo-500 transition-all duration-200',
            parentId: 2,
            roles: ['Super Admin'],
            order: 0
        });
    }

    // Immediately after Dashboard, show Module Assignment for Super Admin only
    if (isSuperAdmin && moduleAssignmentItem) {
        finalMenu.push(moduleAssignmentItem);
    }

    // Show Module Management for Super Admin only (after Module Assignment)
    if (isSuperAdmin && moduleManagementItem) {
        finalMenu.push(moduleManagementItem);
    }

    // Show Search for Admin + Super Admin (after Module Management)
    if (isPrivilegedAdmin && searchItem) {
        finalMenu.push(searchItem);
    }

    // Show PDF to CSV for Admin + Super Admin (after Search)
    if (isPrivilegedAdmin && pdfToCsvItem) {
        finalMenu.push(pdfToCsvItem);
    }

    // Show QR Code Generator for Admin + Super Admin (after PDF to CSV)
    if (isPrivilegedAdmin && qrCodeGeneratorItem) {
        finalMenu.push(qrCodeGeneratorItem);
    }

    // Interview Management and Interview Recordings are now handled dynamically from backend modules
    // They will appear in their appropriate sections based on backend configuration

    // Now add titled sections in the exact order
    // Now add titled sections in the exact order
    orderedSectionNames.forEach(sectionName => {
        const items = sections[sectionName];
        if (items && items.length > 0) {
            finalMenu.push({
                id: `section-${sectionName.toLowerCase().replace(/\s+/g, '-')}`,
                label: sectionName,
                link: '',
                icon: React.createElement("div"),
                className: "text-xs font-semibold text-gray-500 uppercase tracking-wider mt-6 mb-2 px-3",
                isTitle: true
            });
            finalMenu.push(...items);
        }
    });

    return finalMenu;
};

// Main function to build complete menu
export const buildCompleteMenu = (menuPermissions: MenuPermission[]): MenuItem[] => {
    const basicMenu = buildMenuFromPermissions(menuPermissions);
    const finalMenu = addMenuSections(basicMenu);
    return finalMenu;
};
